<template>
  <div v-loading="loading" class="ca-order-edit">
    <template v-if="detail">
      <!-- 标题 + 状态标签 + 订单号 -->
      <div class="ca-order-edit__header">
        <h2 class="ca-order-edit__title">订单编辑-{{ detail.caTypeName }}</h2>
        <span class="ca-order-edit__badge">
          订单状态:<b>{{ statusText }}</b>
        </span>
        <span class="ca-order-edit__code">
          订单号:<b>{{ detail.orderCode }}</b>
        </span>
      </div>

      <!-- 顶部温馨提示 -->
      <p class="ca-order-edit__tip">请按照驳回原因修改订单信息，修改完成后点击【提交订单】</p>

      <!-- 只读订单区块（不可修改）：摘要 + 费用组成 + 商品总计 -->
      <div class="ca-order-edit__block">
        <div class="ca-order-edit__block-title">订单详情</div>
        <div class="ca-order-edit__panel">
          <table class="ca-order-edit__summary">
            <thead>
              <tr>
                <th>服务名称</th>
                <th>业务类型</th>
                <th>CA使用系统</th>
                <th>申请数量</th>
                <th>CA申请有效期</th>
                <th>是否需要加急</th>
                <th>小计</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>CA在线申请</td>
                <td>{{ detail.bizTypeName }}</td>
                <td>{{ detail.platformName }}</td>
                <td>{{ detail.numberVal || '--' }}</td>
                <td>{{ detail.limitName || '--' }}</td>
                <!-- isUrgent 是字符串 '1'/'0'，不能用真值判断（'0' 也是真值） -->
                <td>{{ String(detail.isUrgent) === '1' ? '需要' : '不需要' }}</td>
                <td>¥ {{ detail.totalAmount }}</td>
              </tr>
            </tbody>
          </table>
          <p class="ca-order-edit__fee">
            费用组成：
            <template v-for="(f, i) in feeDetails">
              {{ i ? '；' : '' }}{{ f.name }}：¥ {{ f.amount }}
            </template>
          </p>
          <p class="ca-order-edit__total">
            商品总计： <b>¥ {{ detail.totalAmount }}</b>
          </p>
        </div>
      </div>

      <!-- 可编辑表单：CFCA 与上海CA 两套字段平铺直写（与申请页同口径），按 caType 二选一渲染；
           校验规则与申请页共享（util/caForm.js），上海CA 条件字段显隐按 productValue 白名单
           在本组件内判定，条件字段的 el-form-item 被 v-if 注销后自动不参与校验 -->
      <el-form
        ref="form"
        class="ca-order-edit__form"
        :model="form"
        :rules="rules"
        label-width="170px"
      >
        <!-- ============ CFCA 表单：被授权人信息 + 申请单位信息 ============ -->
        <template v-if="caType === 'CFCA'">
          <div class="ca-order-edit__block">
            <div class="ca-order-edit__block-title">被授权人信息</div>
            <div class="ca-order-edit__form-grid">
              <el-form-item label="被授权经办人姓名" prop="linkMan">
                <el-input v-model.trim="form.linkMan" placeholder="请输入被授权经办人姓名" />
              </el-form-item>
              <el-form-item label="手机号" prop="linkTel">
                <el-input v-model.trim="form.linkTel" placeholder="请输入11位手机号" />
              </el-form-item>
              <el-form-item label="身份证号" prop="identityCode">
                <el-input
                  v-model.trim="form.identityCode"
                  placeholder="请输入18位身份证号"
                  @input="uppercase('identityCode')"
                />
              </el-form-item>
              <el-form-item label="邮箱" prop="linkEmail">
                <el-input
                  v-model.trim="form.linkEmail"
                  placeholder="请输入邮箱"
                  :maxlength="EMAIL_MAX_LENGTH"
                />
              </el-form-item>
              <el-form-item
                label="CA邮寄地址"
                prop="postAddr"
                class="ca-order-edit__form-item--full"
              >
                <el-input
                  v-model.trim="form.postAddr"
                  type="textarea"
                  :rows="2"
                  placeholder="请输入CA邮寄地址（省市区+详细地址）"
                />
              </el-form-item>
            </div>
          </div>
          <div class="ca-order-edit__block">
            <div class="ca-order-edit__block-title">申请单位信息</div>
            <div class="ca-order-edit__form-grid">
              <el-form-item label="单位名称" prop="applyOrgName">
                <el-input
                  v-model.trim="form.applyOrgName"
                  placeholder="请输入单位名称"
                  :maxlength="ORG_NAME_MAX_LENGTH"
                />
              </el-form-item>
              <el-form-item label="统一社会信用代码" prop="licenceCode">
                <el-input
                  v-model.trim="form.licenceCode"
                  placeholder="请输入18位统一社会信用代码"
                  @input="uppercase('licenceCode')"
                />
              </el-form-item>
              <el-form-item label="法定代表人姓名" prop="legalName">
                <el-input v-model.trim="form.legalName" placeholder="请输入法定代表人姓名" />
              </el-form-item>
              <el-form-item label="法定代表人身份证号" prop="legalIdNo">
                <el-input
                  v-model.trim="form.legalIdNo"
                  placeholder="请输入18位身份证号"
                  @input="uppercase('legalIdNo')"
                />
              </el-form-item>
              <el-form-item
                label="营业执照扫描件"
                prop="licenceCodeFile"
                class="ca-order-edit__form-item--full"
              >
                <file-upload
                  :file-id="form.licenceCodeFile"
                  @fileIdCb="(res) => handleFileChange('licenceCodeFile', res)"
                />
              </el-form-item>
              <el-form-item
                label="CA申请授权书"
                prop="authFileId"
                class="ca-order-edit__form-item--full"
              >
                <!-- 授权书仅支持 pdf（与申请页口径一致）；格式提示由 filetypes 自动推导，无需 explain -->
                <file-upload
                  :file-id="form.authFileId"
                  :filetypes="['pdf']"
                  @fileIdCb="(res) => handleFileChange('authFileId', res)"
                />
                <!-- 模板下载链接（loadTemplateFiles 拉取，templateCode === 'SQS' 的文件） -->
                <div v-if="authTemplateFile" class="ca-order-edit__template">
                  <span class="ca-apply__template-link" @click="download(authTemplateFile.fileId)">
                    {{ authTemplateFile.fileName }}模板下载
                  </span>
                </div>
              </el-form-item>
            </div>
          </div>
        </template>

        <!-- ============ 上海CA 表单：经办人/申请单位信息 + 收件信息 ============ -->
        <template v-else>
          <div class="ca-order-edit__block">
            <div class="ca-order-edit__block-title">经办人/申请单位信息</div>
            <div class="ca-order-edit__form-grid">
              <el-form-item label="经办人姓名" prop="linkMan">
                <el-input v-model.trim="form.linkMan" placeholder="请输入经办人姓名" />
              </el-form-item>
              <el-form-item label="经办人身份证号" prop="identityCode">
                <el-input
                  v-model.trim="form.identityCode"
                  placeholder="请输入18位身份证号"
                  @input="uppercase('identityCode')"
                />
              </el-form-item>
              <el-form-item label="经办人手机号" prop="linkTel">
                <el-input v-model.trim="form.linkTel" placeholder="请输入11位手机号" />
              </el-form-item>
              <!-- 经办人身份证扫描件：固定字段（非条件字段），全业务类型恒显必填（与申请页同口径） -->
              <el-form-item label="经办人身份证扫描件" prop="identityFileId">
                <file-upload
                  :file-id="form.identityFileId"
                  @fileIdCb="(res) => handleFileChange('identityFileId', res)"
                />
              </el-form-item>
              <el-form-item label="经办人邮箱" prop="linkEmail">
                <el-input
                  v-model.trim="form.linkEmail"
                  placeholder="请输入邮箱"
                  :maxlength="EMAIL_MAX_LENGTH"
                />
              </el-form-item>
              <!-- 条件字段（显示即必填）：显隐由 showLicenceCodeFile / showLegalFields /
                   showOrgSealFile / showLegalSealFile 各 computed 按当前业务类型的 productValue 白名单控制（与申请页同口径）。
                   法定代表人姓名已全业务放开（恒显必填，不属于条件字段） -->
              <el-form-item label="法定代表人姓名" prop="legalName">
                <el-input v-model.trim="form.legalName" placeholder="请输入法定代表人姓名" />
              </el-form-item>
              <el-form-item v-if="showLegalFields" label="法定代表人身份证号" prop="legalIdNo">
                <el-input
                  v-model.trim="form.legalIdNo"
                  placeholder="请输入18位身份证号"
                  @input="uppercase('legalIdNo')"
                />
              </el-form-item>
              <el-form-item label="单位名称" prop="applyOrgName">
                <el-input
                  v-model.trim="form.applyOrgName"
                  placeholder="请输入单位名称"
                  :maxlength="ORG_NAME_MAX_LENGTH"
                />
              </el-form-item>
              <el-form-item label="统一社会信用代码" prop="licenceCode">
                <el-input
                  v-model.trim="form.licenceCode"
                  placeholder="请输入18位统一社会信用代码"
                  @input="uppercase('licenceCode')"
                />
              </el-form-item>
              <!-- 营业执照扫描件：仅「申请单位证书 / 续费单位证书」业务类型展示（显示即必填） -->
              <el-form-item
                v-if="showLicenceCodeFile"
                label="营业执照扫描件"
                prop="licenceCodeFile"
              >
                <file-upload
                  :file-id="form.licenceCodeFile"
                  @fileIdCb="(res) => handleFileChange('licenceCodeFile', res)"
                />
              </el-form-item>
              <el-form-item
                v-if="showLegalFields"
                label="法定代表人身份证扫描件"
                prop="legalIdFileId"
              >
                <file-upload
                  :file-id="form.legalIdFileId"
                  @fileIdCb="(res) => handleFileChange('legalIdFileId', res)"
                />
              </el-form-item>
              <el-form-item v-if="showOrgSealFile" label="单位公章图片" prop="orgSealFileId">
                <file-upload
                  :file-id="form.orgSealFileId"
                  @fileIdCb="(res) => handleFileChange('orgSealFileId', res)"
                />
                <!-- 模板下载链接（templateFileList 中 templateCode === 'DWGZ' 的文件） -->
                <div v-if="orgSealTemplateFile" class="ca-order-edit__template">
                  <span class="ca-apply__template-link" @click="download(orgSealTemplateFile.fileId)">
                    {{ orgSealTemplateFile.fileName }}模板下载
                  </span>
                </div>
              </el-form-item>
              <el-form-item
                v-if="showLegalSealFile"
                label="法人私章/手写签字图片"
                prop="legalSealFileId"
              >
                <file-upload
                  :file-id="form.legalSealFileId"
                  @fileIdCb="(res) => handleFileChange('legalSealFileId', res)"
                />
              </el-form-item>
            </div>
          </div>
          <div class="ca-order-edit__block">
            <div class="ca-order-edit__block-title">收件信息</div>
            <div class="ca-order-edit__form-grid">
              <el-form-item label="收件人姓名" prop="receiverName">
                <el-input v-model.trim="form.receiverName" placeholder="请输入收件人姓名" />
              </el-form-item>
              <el-form-item label="收件人手机号" prop="receiverMobile">
                <el-input v-model.trim="form.receiverMobile" placeholder="请输入11位手机号" />
              </el-form-item>
              <el-form-item
                label="收件地址"
                prop="postAddr"
                class="ca-order-edit__form-item--full"
              >
                <el-input
                  v-model.trim="form.postAddr"
                  type="textarea"
                  :rows="2"
                  placeholder="请输入收件地址（省/市/区县+详细地址）"
                />
              </el-form-item>
            </div>
          </div>
        </template>
      </el-form>

      <div class="ca-order-edit__footer">
        <el-button class="ca-order-edit__cancel" @click="goBack">取消</el-button>
        <el-button type="danger" :loading="submitting" @click="handleSubmit"> 提交订单 </el-button>
      </div>
    </template>
  </div>
</template>

<script>
import FileUpload from '../components/FileUpload.vue'
import { getOrderDetail, resubmitOrder, getPageData } from '@/api/ca'
import { CA_TYPE, EMAIL_MAX_LENGTH, ORG_NAME_MAX_LENGTH, TEMPLATE_CODE,
  CA_FORM_KEYS,
  CFCA_FORM_KEYS,
  SHCA_FORM_KEYS,
  buildCaFormRules,
  orderStatusText } from '../util/caForm'
import { download } from '@/shared/file'

export default {
  name: 'CaOrderEdit',
  components: { FileUpload },
  data() {
    return {
      EMAIL_MAX_LENGTH,
      ORG_NAME_MAX_LENGTH,
      detail: null, // 订单详情原对象：顶部只读区块 + 提交时的订单上下文都从这里取
      form: {}, // 可编辑表单值：进页面时按 CA_FORM_KEYS 从 detail 预填（同时完成响应式预注册）
      loading: false, // 详情加载中 → 整页 loading
      submitting: false, // 提交中 → 提交按钮 loading 防重复
      authTemplateFile: null, // CA申请授权书模板文件（CFCA 专有字段旁的模板下载链接；上海CA 为 null 不显示）
      orgSealTemplateFile: null, // 单位公章模板文件（上海CA 单位公章图片字段旁的模板下载链接；CFCA 为 null 不显示）
    }
  },
  computed: {
    orderId() {
      return this.$route.query.orderId
    },
    // CA 厂商（CFCA / 上海CA）：caTypeName 仅这两种，优先按 CFCA 判断；决定渲染哪套表单与顶部提示文案
    caType() {
      return this.detail && this.detail.caTypeName === 'CFCA' ? CA_TYPE.CFCA : CA_TYPE.SHCA
    },
    // 校验规则：按 caType 生成（与申请页共享，见 util/caForm.js）；
    // 上海CA 条件字段靠模板 v-if 注销 el-form-item 不参与校验，无需按业务类型裁剪
    rules() {
      return buildCaFormRules(this.caType)
    },
    // 业务类型 value：详情接口直接下发 productValue（字符串形态，如 '4'；含义见
    // util/caForm.js 的 BIZ_TYPE_OPTIONS），转数字后仅上海CA 用于条件字段显隐
    productValue() {
      const v = this.detail && this.detail.productValue
      return v == null || v === '' ? null : Number(v)
    },
    // 上海CA 条件字段显隐（显示即必填），按 productValue 白名单判定（与申请页同口径）：
    // 法定代表人身份证号 + 身份证扫描件（法定代表人姓名已全业务放开，恒显必填不在此列）：
    // value ∈ {1 申请单位证书+法人证书, 2 续费单位证书+法人证书, 5 申请法人证书, 6 续费法人证书}
    showLegalFields() {
      return [1, 2, 5, 6].includes(this.productValue)
    },
    // 营业执照扫描件：value ∈ {3 申请单位证书, 4 续费单位证书}（与申请页同口径）
    showLicenceCodeFile() {
      return [3, 4].includes(this.productValue)
    },
    // 单位公章图片：value ∈ {1 申请单位证书+法人证书, 3 申请单位证书, 7 申请单位证书(境外)}
    showOrgSealFile() {
      return [1, 3, 7].includes(this.productValue)
    },
    // 法人私章/手写签字图片：value ∈ {1 申请单位证书+法人证书, 5 申请法人证书}
    showLegalSealFile() {
      return [1, 5].includes(this.productValue)
    },
    // 状态文字：认证驳回(80) 按「待认证」展示，其余优先后端下发的 orderStatusName
    // （口径见 util/caForm.js orderStatusText）
    statusText() {
      return orderStatusText(this.detail)
    },
    // 费用组成明细（只读展示，如「CA费用：¥ xxx；加急费用：¥ xxx」）：
    // 详情 VO 只有 totalAmount / productAmount / isUrgent，在此拆成「CA费用 + 加急费用」两行
    feeDetails() {
      const d = this.detail || {}
      const fees = []
      if (d.productAmount != null) fees.push({ name: 'CA费用', amount: d.productAmount })
      const urgentFee = Number(d.totalAmount) - Number(d.productAmount)
      if (String(d.isUrgent) === '1' && urgentFee > 0) {
        fees.push({ name: '加急费用', amount: urgentFee.toFixed(2) })
      }
      return fees
    },
  },
  created() {
    if (!this.orderId) {
      this.$message.warning('订单参数缺失，已返回订单列表')
      this.$router.replace({ name: 'ca-orders' })
      return
    }
    this.loadDetail()
  },
  methods: {
    download,
    loadDetail() {
      this.loading = true
      getOrderDetail({ orderId: this.orderId })
        .then((res) => {
          if (!res.data) {
            this.$message.error('订单不存在或已删除')
            this.$router.replace({ name: 'ca-orders' })
            return
          }
          this.detail = res.data
          // 表单预填原订单值：按 CA_FORM_KEYS 逐个取详情同名字段、缺的补空串
          // （一次性 $set 全部 key 同时完成 Vue 2 响应式预注册；地址类是整串文本，直接回填）
          CA_FORM_KEYS.forEach((key) => this.$set(this.form, key, res.data[key] || ''))
          this.loadTemplateFiles()
        })
        .finally(() => {
          this.loading = false
        })
    },
    // 模板下载链接（templateFileList 中 templateCode 匹配的文件）：
    // CFCA 取 SQS（授权书），上海CA 取 DWGZ（单位公章）
    loadTemplateFiles() {
      const code = this.$store.state.ca.customerCode
      if (!code) return
      getPageData({ customerCode: code })
        .then((res) => {
          const files = (res.data && res.data.templateFileList) || []
          if (this.caType === CA_TYPE.CFCA) {
            this.authTemplateFile = files.find((f) => f.templateCode === TEMPLATE_CODE.AUTH) || null
          } else {
            this.orgSealTemplateFile =
              files.find((f) => f.templateCode === TEMPLATE_CODE.ORG_SEAL) || null
          }
        })
    },
    // 身份证号 / 统一社会信用代码输入时自动转大写（这两个字段的校验正则只认大写字母）
    uppercase(key) {
      this.form[key] = (this.form[key] || '').toUpperCase()
    },
    handleFileChange(key, res) {
      this.$set(this.form, key, res.url || '')
      this.$refs.form && this.$refs.form.validateField(key)
    },
    goBack() {
      this.$router.push({ name: 'ca-orders' })
    },
    // 提交：二次确认 → resubmit 改原单（仅已驳回订单可用）→ 成功进详情页（状态=审核中）
    handleSubmit() {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        this.$confirm('请确认订单修改信息正确，提交后无法修改', '提示', {
          confirmButtonText: '提交订单',
          cancelButtonText: '取消',
          type: 'warning',
        })
          .then(() => this.doSubmit())
          .catch(() => {})
      })
    },
    doSubmit() {
      this.submitting = true
      // 已驳回订单走 resubmit（文档 §4.10）：只提交 orderId + 表单字段（非空覆盖），
      // 商品/金额/支付方式不可改；表单 key 与后端一致，直接展开提交。
      // 后端校验通过后订单回到「审核中」重新审核。
      // form 里是两类 CA 的全量 key（预注册需要），提交前按当前厂商拣选对应 key 集，
      // 避免把另一类 CA 的空 key 带进 payload
      const keys = this.caType === CA_TYPE.SHCA ? SHCA_FORM_KEYS : CFCA_FORM_KEYS
      const form = {}
      keys.forEach((key) => {
        form[key] = this.form[key]
      })
      resubmitOrder({
        orderId: this.orderId,
        ...form,
      })
        .then(() => {
          this.$message.success('订单提交成功')
          this.$router.push({
            name: 'ca-order-detail',
            query: { orderId: this.orderId },
          })
        })
        .finally(() => {
          this.submitting = false
        })
    },
  },
}
</script>
